import { create } from 'zustand';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  DexiePropertyRepository,
  HybridPropertyRepository,
  SupabasePropertyRepository,
  getExportImportService,
  type PropertyRepository,
  type PropertyRecord,
  type PropertySummary,
  type CreatePropertyRecord,
  type PropertyQueryFilters,
  type PropertySortOptions,
  type PropertyStatus,
  type SupabaseClientWithSchema,
} from '@roi/data';

interface SyncState {
  isSyncing: boolean;
  pendingCount: number;
  lastSyncTime: Date | null;
  syncError: string | null;
}

interface LibraryState {
  // Data
  properties: PropertySummary[];
  selectedProperty: PropertyRecord | null;
  totalCount: number;
  allTags: string[];
  allCities: string[];
  statusCounts: Record<PropertyStatus, number>;

  // Plan limits
  propertyLimit: number;
  totalPropertyCount: number;

  // Filters & Sort
  filters: PropertyQueryFilters;
  sort: PropertySortOptions;
  page: number;
  pageSize: number;

  // UI state
  isLoading: boolean;
  error: string | null;
  isExporting: boolean;
  isImporting: boolean;

  // Sync state
  sync: SyncState;
  isAuthenticated: boolean;

  // Actions
  loadProperties: () => Promise<void>;
  loadProperty: (id: string) => Promise<PropertyRecord | null>;
  findByAddress: (address: string) => Promise<PropertyRecord | null>;
  createProperty: (data: CreatePropertyRecord) => Promise<PropertyRecord>;
  updateProperty: (id: string, data: Partial<CreatePropertyRecord>) => Promise<PropertyRecord>;
  deleteProperty: (id: string) => Promise<void>;
  bulkDelete: (ids: string[]) => Promise<void>;
  archiveProperty: (id: string) => Promise<void>;
  restoreProperty: (id: string) => Promise<void>;
  toggleFavorite: (id: string) => Promise<void>;
  setFilters: (filters: Partial<PropertyQueryFilters>) => void;
  setSort: (sort: PropertySortOptions) => void;
  setPage: (page: number) => void;
  clearFilters: () => void;
  refreshMetadata: () => Promise<void>;
  exportToJson: () => Promise<void>;
  exportToCsv: () => Promise<void>;
  importFromJson: (file: File) => Promise<{ imported: number; skipped: number; errors: string[] }>;
  importFromCsv: (file: File) => Promise<{ imported: number; skipped: number; errors: string[] }>;
  importFromFile: (file: File) => Promise<{ imported: number; skipped: number; errors: string[] }>;
  clearError: () => void;

  // Auth/Sync actions
  setAuthenticated: (userId: string | null, supabaseClient: SupabaseClient | null) => void;
  triggerSync: () => Promise<void>;
  pullFromCloud: () => Promise<void>;
  pushToCloud: () => Promise<void>;
}

// Repository instances
const localRepo = new DexiePropertyRepository();
let hybridRepo: HybridPropertyRepository | null = null;
let currentRepo: PropertyRepository = localRepo;

const exportService = getExportImportService();

// Free plan limits
const FREE_PLAN_PROPERTY_LIMIT = 5;

const defaultFilters: PropertyQueryFilters = {
  status: 'active',
};

const defaultSort: PropertySortOptions = {
  field: 'updatedAt',
  direction: 'desc',
};

export const useLibraryStore = create<LibraryState>((set, get) => ({
  // Initial state
  properties: [],
  selectedProperty: null,
  totalCount: 0,
  allTags: [],
  allCities: [],
  statusCounts: { active: 0, archived: 0, draft: 0 },

  // Plan limits
  propertyLimit: FREE_PLAN_PROPERTY_LIMIT,
  totalPropertyCount: 0,
  filters: defaultFilters,
  sort: defaultSort,
  page: 0,
  pageSize: 20,
  isLoading: false,
  error: null,
  isExporting: false,
  isImporting: false,
  sync: {
    isSyncing: false,
    pendingCount: 0,
    lastSyncTime: null,
    syncError: null,
  },
  isAuthenticated: false,

  // Auth/Sync actions
  setAuthenticated: (userId: string | null, supabaseClient: SupabaseClient | null) => {
    if (userId && supabaseClient) {
      // User logged in - create hybrid repository with cloud sync
      const cloudRepo = new SupabasePropertyRepository(
        supabaseClient as SupabaseClientWithSchema,
        userId
      );

      hybridRepo = new HybridPropertyRepository(localRepo, cloudRepo, {
        autoSync: true,
        syncIntervalMs: 30000,
        onSyncStatusChange: (isSyncing, pendingCount) => {
          set((state) => ({
            sync: {
              ...state.sync,
              isSyncing,
              pendingCount,
              lastSyncTime: isSyncing ? state.sync.lastSyncTime : new Date(),
            },
          }));
        },
      });

      currentRepo = hybridRepo;
      set({ isAuthenticated: true });

      // On login: first pull cloud data to local, then push any local changes
      // This ensures we get existing cloud data before syncing
      hybridRepo.pullFromCloud().then(() => {
        // After pulling, sync any pending local changes to cloud
        hybridRepo?.syncPendingOperations();
        // Reload to show merged data
        get().loadProperties();
        get().refreshMetadata();
      }).catch((error) => {
        console.error('Failed to pull from cloud on login:', error);
        set((state) => ({
          sync: {
            ...state.sync,
            syncError: error instanceof Error ? error.message : 'Failed to sync from cloud',
          },
        }));
      });
    } else {
      // User logged out - switch back to local only
      if (hybridRepo) {
        hybridRepo.dispose();
        hybridRepo = null;
      }
      currentRepo = localRepo;
      set({
        isAuthenticated: false,
        sync: {
          isSyncing: false,
          pendingCount: 0,
          lastSyncTime: null,
          syncError: null,
        },
      });
    }

    // Reload data with new repository
    get().loadProperties();
    get().refreshMetadata();
  },

  triggerSync: async () => {
    if (!hybridRepo) return;

    set((state) => ({
      sync: { ...state.sync, syncError: null },
    }));

    try {
      await hybridRepo.syncPendingOperations();
    } catch (error) {
      set((state) => ({
        sync: {
          ...state.sync,
          syncError: error instanceof Error ? error.message : 'Sync failed',
        },
      }));
    }
  },

  pullFromCloud: async () => {
    if (!hybridRepo) return;

    set({ isLoading: true, error: null });
    try {
      const result = await hybridRepo.pullFromCloud();
      if (result.errors.length > 0) {
        set({ error: `Some errors during pull: ${result.errors.join(', ')}` });
      }
      await get().loadProperties();
      await get().refreshMetadata();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to pull from cloud',
      });
    } finally {
      set({ isLoading: false });
    }
  },

  pushToCloud: async () => {
    if (!hybridRepo) return;

    set({ isLoading: true, error: null });
    try {
      const result = await hybridRepo.pushToCloud();
      if (result.errors.length > 0) {
        set({ error: `Some errors during push: ${result.errors.join(', ')}` });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to push to cloud',
      });
    } finally {
      set({ isLoading: false });
    }
  },

  // Actions
  loadProperties: async () => {
    set({ isLoading: true, error: null });
    try {
      const { filters, sort, page, pageSize } = get();
      const result = await currentRepo.list({
        filters,
        sort,
        pagination: { offset: page * pageSize, limit: pageSize },
      });
      set({
        properties: result.items,
        totalCount: result.total,
        isLoading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load properties',
        isLoading: false,
      });
    }
  },

  loadProperty: async (id: string) => {
    try {
      const property = await currentRepo.getById(id);
      set({ selectedProperty: property });
      return property;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load property',
      });
      return null;
    }
  },

  findByAddress: async (address: string) => {
    try {
      const normalizedAddress = address.trim().toLowerCase();
      if (!normalizedAddress) return null;

      // Search all properties (no status filter) to find by address
      const result = await currentRepo.list({
        filters: {},
        pagination: { offset: 0, limit: 1000 },
      });

      const match = result.items.find(
        (p) => p.address?.trim().toLowerCase() === normalizedAddress
      );

      if (match) {
        return await currentRepo.getById(match.id);
      }
      return null;
    } catch (error) {
      console.error('Failed to find property by address:', error);
      return null;
    }
  },

  createProperty: async (data: CreatePropertyRecord) => {
    // Check free plan limit
    const { totalPropertyCount, propertyLimit } = get();
    if (totalPropertyCount >= propertyLimit) {
      const error = new Error(`Free plan limit reached. You can save up to ${propertyLimit} properties. Please upgrade to save more or delete existing properties.`);
      set({ error: error.message });
      throw error;
    }

    set({ isLoading: true, error: null });
    try {
      const property = await currentRepo.create(data);
      await get().loadProperties();
      await get().refreshMetadata();
      return property;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to create property',
        isLoading: false,
      });
      throw error;
    }
  },

  updateProperty: async (id: string, data: Partial<CreatePropertyRecord>) => {
    set({ isLoading: true, error: null });
    try {
      const property = await currentRepo.update({ id, ...data });
      await get().loadProperties();
      if (get().selectedProperty?.id === id) {
        set({ selectedProperty: property });
      }
      return property;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to update property',
        isLoading: false,
      });
      throw error;
    }
  },

  deleteProperty: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await currentRepo.delete(id);
      if (get().selectedProperty?.id === id) {
        set({ selectedProperty: null });
      }
      await get().loadProperties();
      await get().refreshMetadata();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete property',
        isLoading: false,
      });
      throw error;
    }
  },

  bulkDelete: async (ids: string[]) => {
    set({ isLoading: true, error: null });
    try {
      await currentRepo.bulkDelete(ids);
      if (get().selectedProperty && ids.includes(get().selectedProperty!.id)) {
        set({ selectedProperty: null });
      }
      await get().loadProperties();
      await get().refreshMetadata();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete properties',
        isLoading: false,
      });
      throw error;
    }
  },

  archiveProperty: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await currentRepo.archive(id);
      await get().loadProperties();
      await get().refreshMetadata();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to archive property',
        isLoading: false,
      });
      throw error;
    }
  },

  restoreProperty: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await currentRepo.restore(id);
      await get().loadProperties();
      await get().refreshMetadata();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to restore property',
        isLoading: false,
      });
      throw error;
    }
  },

  toggleFavorite: async (id: string) => {
    try {
      await currentRepo.toggleFavorite(id);
      // Update local state optimistically
      set((state) => ({
        properties: state.properties.map((p) =>
          p.id === id ? { ...p, isFavorite: !p.isFavorite } : p
        ),
      }));
      // Reload to ensure filtered views are updated correctly
      get().loadProperties();
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to toggle favorite',
      });
    }
  },

  setFilters: (newFilters: Partial<PropertyQueryFilters>) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      page: 0, // Reset to first page when filters change
    }));
    get().loadProperties();
  },

  setSort: (sort: PropertySortOptions) => {
    set({ sort, page: 0 });
    get().loadProperties();
  },

  setPage: (page: number) => {
    set({ page });
    get().loadProperties();
  },

  clearFilters: () => {
    set({ filters: defaultFilters, page: 0 });
    get().loadProperties();
  },

  refreshMetadata: async () => {
    try {
      const [tags, cities, counts] = await Promise.all([
        currentRepo.getAllTags(),
        currentRepo.getAllCities(),
        currentRepo.getCountByStatus(),
      ]);
      // Calculate total property count across all statuses
      const totalPropertyCount = counts.active + counts.archived + counts.draft;
      set({ allTags: tags, allCities: cities, statusCounts: counts, totalPropertyCount });
    } catch (error) {
      console.error('Failed to refresh metadata:', error);
    }
  },

  exportToJson: async () => {
    set({ isExporting: true, error: null });
    try {
      const allProperties = await currentRepo.exportAll();
      const json = exportService.exportToJson(allProperties);
      const filename = `roi-properties-${new Date().toISOString().slice(0, 10)}.json`;
      exportService.downloadFile(json, filename, 'application/json');
      set({ isExporting: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to export',
        isExporting: false,
      });
    }
  },

  exportToCsv: async () => {
    set({ isExporting: true, error: null });
    try {
      const allProperties = await currentRepo.exportAll();
      const csv = exportService.exportToCsv(allProperties);
      const filename = `roi-properties-${new Date().toISOString().slice(0, 10)}.csv`;
      exportService.downloadFile(csv, filename, 'text/csv');
      set({ isExporting: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to export',
        isExporting: false,
      });
    }
  },

  importFromJson: async (file: File) => {
    set({ isImporting: true, error: null });
    try {
      const content = await file.text();
      const records = exportService.parseJson(content);
      const result = await currentRepo.importAll(records, { overwrite: false });
      await get().loadProperties();
      await get().refreshMetadata();
      set({ isImporting: false });
      return result;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to import',
        isImporting: false,
      });
      throw error;
    }
  },

  importFromCsv: async (file: File) => {
    set({ isImporting: true, error: null });
    try {
      const content = await file.text();
      const partialRecords = exportService.parseCsv(content);
      const result = { imported: 0, skipped: 0, errors: [] as string[] };

      for (let i = 0; i < partialRecords.length; i++) {
        const record = partialRecords[i];
        try {
          // Ensure required fields have defaults
          const createRecord = {
            name: record.name || `Imported Property ${i + 1}`,
            address: record.address,
            city: record.city,
            state: record.state,
            zipCode: record.zipCode,
            propertyType: record.propertyType,
            status: record.status || 'active',
            isFavorite: record.isFavorite || false,
            tags: record.tags || [],
            notes: record.notes,
            sourceUrl: record.sourceUrl,
            dealInput: record.dealInput!,
          };
          await currentRepo.create(createRecord);
          result.imported++;
        } catch (error) {
          result.errors.push(`Row ${i + 2}: ${error instanceof Error ? error.message : 'Unknown error'}`);
          result.skipped++;
        }
      }

      await get().loadProperties();
      await get().refreshMetadata();
      set({ isImporting: false });
      return result;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to import CSV',
        isImporting: false,
      });
      throw error;
    }
  },

  importFromFile: async (file: File) => {
    const fileName = file.name.toLowerCase();
    if (fileName.endsWith('.csv')) {
      return get().importFromCsv(file);
    } else if (fileName.endsWith('.json')) {
      return get().importFromJson(file);
    } else {
      const error = new Error('Unsupported file format. Please use .json or .csv files.');
      set({ error: error.message });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));

// Initialize store - load data on first access
setTimeout(() => {
  useLibraryStore.getState().loadProperties();
  useLibraryStore.getState().refreshMetadata();
}, 0);
